import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { toast } from "sonner";
import {
  Target, TrendingUp, DollarSign, Users, CheckCircle,
  Plus, Pencil, Trophy
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { API } from "../../apiConfig";


const PerformanceGoals = ({ token }) => {
  const [goals, setGoals] = useState([]);
  const [projection, setProjection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewGoal, setShowNewGoal] = useState(false);
  const [newGoal, setNewGoal] = useState({ metric: "sales", target: "", period: "monthly" });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [goalsRes, projectionRes] = await Promise.all([
        axios.get(`${API}/sales-agent/goals/progress`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/sales-agent/commission-projection`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setGoals(goalsRes.data.goals || []);
      setProjection(projectionRes.data);
    } catch (error) {
      console.error("Error fetching goals:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGoal = async () => {
    if (!newGoal.target) {
      toast.error("Please enter a target value");
      return;
    }
    try {
      await axios.post(`${API}/sales-agent/goals`, {
        metric: newGoal.metric,
        target: parseFloat(newGoal.target),
        period: newGoal.period
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Goal created!");
      setShowNewGoal(false);
      setNewGoal({ metric: "sales", target: "", period: "monthly" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create goal");
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getMetricIcon = (metric) => {
    switch (metric) {
      case "sales": return <DollarSign className="w-4 h-4" />;
      case "leads": return <Users className="w-4 h-4" />;
      case "conversions": return <CheckCircle className="w-4 h-4" />;
      default: return <Target className="w-4 h-4" />;
    }
  };

  const getMetricLabel = (metric) => {
    switch (metric) {
      case "sales": return "Sales Target";
      case "leads": return "New Leads";
      case "conversions": return "Conversions";
      default: return metric;
    }
  };

  const formatValue = (metric, value) => {
    if (metric === "sales") return formatPrice(value);
    return value.toString();
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Commission Projection */}
      {projection && (
        <Card className="rounded-2xl bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Commission Projection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Pipeline Value</p>
                <p className="text-xl font-bold">{formatPrice(projection.total_pipeline_value)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Expected Commission</p>
                <p className="text-xl font-bold text-primary">{formatPrice(projection.expected_commission)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Pending Commission</p>
                <p className="text-xl font-bold">{formatPrice(projection.current_pending)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Projected Total</p>
                <p className="text-xl font-bold text-green-600">{formatPrice(projection.projected_total)}</p>
              </div>
            </div>
            
            {projection.by_status && Object.keys(projection.by_status).length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-muted-foreground mb-2">Pipeline by Stage</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(projection.by_status).map(([status, data]) => (
                    <Badge key={status} variant="outline" className="text-xs">
                      {status}: {data.count} ({formatPrice(data.weighted)} weighted)
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Performance Goals */}
      <Card className="rounded-2xl">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Performance Goals
            </CardTitle>
            <Dialog open={showNewGoal} onOpenChange={setShowNewGoal}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-1" /> Add Goal
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Set New Goal</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <label className="text-sm font-medium">Metric</label>
                    <Select value={newGoal.metric} onValueChange={(v) => setNewGoal({ ...newGoal, metric: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sales">Sales ($)</SelectItem>
                        <SelectItem value="leads">New Leads</SelectItem>
                        <SelectItem value="conversions">Conversions</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Target</label>
                    <Input
                      type="number"
                      placeholder={newGoal.metric === "sales" ? "e.g., 50000" : "e.g., 20"}
                      value={newGoal.target}
                      onChange={(e) => setNewGoal({ ...newGoal, target: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Period</label>
                    <Select value={newGoal.period} onValueChange={(v) => setNewGoal({ ...newGoal, period: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleCreateGoal} className="w-full">Create Goal</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {goals.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Target className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No goals set yet. Create your first goal!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {goals.map((goal) => (
                <div key={goal.goal_id} className="p-4 border rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getMetricIcon(goal.metric)}
                      <span className="font-medium">{getMetricLabel(goal.metric)}</span>
                      <Badge variant="outline" className="text-xs">{goal.period}</Badge>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {formatValue(goal.metric, goal.current || 0)} / {formatValue(goal.metric, goal.target)}
                    </span>
                  </div>
                  <Progress value={goal.progress_percent || 0} className="h-2" />
                  <p className="text-xs text-muted-foreground mt-1 text-right">
                    {(goal.progress_percent || 0).toFixed(0)}% complete
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformanceGoals;
